import { param } from 'saddlebag-url';

import type { FareAttribute } from '@skyscanner-internal/global-components/types/fareAttributes';

type TripType = 'one-way' | 'return' | 'multi-destination';

type UrlBuilderStrings = {
  transport: string;
  flights: string;
  flights_from: string;
};

type Leg = {
  originId: string;
  destinationId: string;
  date: string;
};

type UrlContext = {
  tripType: TripType;
  originId?: string;
  destinationId?: string | null;
  outboundDate?: string;
  inboundDate?: string;
  preferDirects?: boolean;
  outboundAlts?: boolean;
  inboundAlts?: boolean;
  alternativeorigins?: string[];
  alternativedestinations?: string[];
  adultsV2?: number;
  childrenV2?: number[];
  cabinclass?: string;
  legs?: Leg[];
  seoAirlineCode?: string;
  targetPage?: string;
  fareAttributes?: FareAttribute[];
  [key: string]: unknown;
};

type QueryParams = {
  adultsV2: number;
  childrenV2: string;
  cabinclass: string;
  rtn?: number;
  preferdirects?: boolean;
  outboundaltsenabled?: boolean;
  inboundaltsenabled?: boolean;
  alternativeorigins?: string;
  alternativedestinations?: string;
  oym?: string;
  iym?: string;
  seo_airline?: string;
  [key: string]: unknown;
};

const DAY = 'day';
const MONTH = 'month';

const getDateType = (
  date?: string | null,
): typeof DAY | typeof MONTH | null => {
  if (date && date.length === 10) {
    return DAY;
  }
  if (date && date.length === 7) {
    return MONTH;
  }
  return null;
};

const formatDate = (date: string | undefined): string => {
  if (!date) {
    throw new Error('invalid date format: undefined');
  }

  const type = getDateType(date);
  if (type) {
    return date.replaceAll('-', '').substring(2);
  }
  throw new Error(`invalid date format: ${date}`);
};

function isMultiDestination(context: UrlContext): boolean {
  return context.tripType === 'multi-destination';
}

function isReturn(context: UrlContext): boolean {
  return context.tripType === 'return';
}

const buildMapUrl = (context: UrlContext): string => {
  const funnelInfo = {
    outboundDate: context.outboundDate,
    outboundPlace: context.originId,
    preferDirects: context.preferDirects,
  };
  return `/inspire/map?${param(funnelInfo, true)}`;
};

const buildPlacePath = (context: UrlContext): string => {
  if (!context.originId) {
    throw new Error('originId is required');
  }

  let path = `/${context.originId.toLowerCase()}`;
  if (context.destinationId) {
    path += `/${context.destinationId.toLowerCase()}`;
  }
  return path;
};

const buildDatePath = (context: UrlContext): string => {
  let url = '';
  if (getDateType(context.outboundDate) === DAY) {
    url += `/${formatDate(context.outboundDate)}`;
    if (isReturn(context) && getDateType(context.inboundDate) === DAY) {
      url += `/${formatDate(context.inboundDate)}`;
    }
  }
  return url;
};

const buildUrlPathPrefix = (
  strings: UrlBuilderStrings,
  context: UrlContext,
): string => {
  if (isMultiDestination(context)) {
    return `/${strings.transport}/d`;
  }
  if (context.destinationId) {
    return `/${strings.transport}/${strings.flights}`;
  }
  return `/${strings.transport}/${strings.flights_from}`;
};

const buildUrlPath = (context: UrlContext): string => {
  if (isMultiDestination(context)) {
    if (!context.legs?.length) {
      return '';
    }
    const segments = context.legs.map((leg) => {
      const origin = leg.originId.toLowerCase();
      const destination = leg.destinationId.toLowerCase();
      return `/${origin}/${leg.date}/${destination}`;
    });
    return segments.join('');
  }
  return `${buildPlacePath(context)}${buildDatePath(context)}/`;
};

const buildQueryString = (context: UrlContext): string => {
  const query: QueryParams = {
    adultsV2: context.adultsV2 ?? 1,
    childrenV2: (context.childrenV2 ?? []).join('|'),
    cabinclass: context.cabinclass ?? 'economy',
  };

  if (!isMultiDestination(context)) {
    query.rtn = isReturn(context) ? 1 : 0;
    query.preferdirects = Boolean(context.preferDirects);
    query.outboundaltsenabled = Boolean(context.outboundAlts);
    query.inboundaltsenabled = Boolean(context.inboundAlts);

    if (context.alternativeorigins?.length) {
      query.alternativeorigins = context.alternativeorigins.join(',');
    }

    if (context.alternativedestinations?.length) {
      query.alternativedestinations = context.alternativedestinations.join(',');
    }

    if (getDateType(context.outboundDate) === MONTH) {
      query.oym = formatDate(context.outboundDate);
    }
    if (getDateType(context.inboundDate) === MONTH && isReturn(context)) {
      query.iym = formatDate(context.inboundDate);
    }
  }

  if (context.seoAirlineCode) {
    query.seo_airline = context.seoAirlineCode;
  }

  const lowercasedQuery = `?${param(query, true)}`.toLowerCase();

  if (context.fareAttributes?.length) {
    return `${lowercasedQuery}&fare-attributes=${encodeURIComponent(context.fareAttributes.join(','))}`;
  }

  return lowercasedQuery;
};

const buildUrl = (strings: UrlBuilderStrings, context?: UrlContext): string => {
  if (!context) {
    throw new Error('Context not set');
  }

  if ((context.targetPage || '').toLowerCase() === 'inspiration-map') {
    return buildMapUrl(context);
  }

  if (!['multi-destination', 'return', 'one-way'].includes(context.tripType)) {
    throw new Error('Invalid tripType');
  }

  return `${buildUrlPathPrefix(strings, context)}${buildUrlPath(
    context,
  )}${buildQueryString(context)}`;
};

export {
  buildUrlPath,
  buildPlacePath,
  buildUrlPathPrefix,
  buildMapUrl,
  buildQueryString,
};
export type { UrlContext, UrlBuilderStrings, Leg };
export default buildUrl;
